/**
 * AIアシスタントパネルコンポーネント
 *
 * 右サイドに表示されるチャットパネル
 */

import { useState, useRef, useEffect } from "react";
import {
  Paper,
  Stack,
  ScrollArea,
  Alert,
  Transition,
} from "@mantine/core";
import { AlertCircle } from "lucide-react";
import { useAssistantContext } from "../contexts/AssistantContext";
import { AttachedImage } from "../services/assistantService";
import { MessageBubble, TypingIndicator } from "./assistant/MessageBubble";
import { SessionHistoryDrawer } from "./assistant/SessionHistoryDrawer";
import { ChatPanelHeader } from "./assistant/ChatPanelHeader";
import { EmptyChatState } from "./assistant/EmptyChatState";
import { ChatInputArea } from "./assistant/ChatInputArea";

// 最大添付画像数
const MAX_IMAGES = 3;

interface AssistantPanelProps {
  opened: boolean;
  onClose: () => void;
}

/**
 * AIアシスタントパネル
 */
export function AssistantPanel({ opened, onClose }: AssistantPanelProps) {
  const {
    messages,
    isLoading,
    error,
    currentSessionId,
    sessions,
    isLoadingSessions,
    sendChat,
    clearError,
    loadSessions,
    selectSession,
    startNewSession,
  } = useAssistantContext();

  const [inputValue, setInputValue] = useState("");
  const [historyOpened, setHistoryOpened] = useState(false);
  const [attachedImages, setAttachedImages] = useState<AttachedImage[]>([]);
  const scrollAreaRef = useRef<HTMLDivElement>(null);

  // 新しいメッセージでスクロール
  useEffect(() => {
    if (scrollAreaRef.current) {
      scrollAreaRef.current.scrollTo({
        top: scrollAreaRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  }, [messages, isLoading]);

  // パネルが開いた時にセッション一覧を更新
  useEffect(() => {
    if (opened) {
      loadSessions();
    }
  }, [opened, loadSessions]);

  /**
   * ファイルをBase64に変換
   */
  const fileToBase64 = (file: File): Promise<AttachedImage> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        // data:image/png;base64,XXXX の形式から base64 部分を抽出
        const base64 = result.split(",")[1];
        resolve({
          mimeType: file.type as
            | "image/png"
            | "image/jpeg"
            | "image/webp"
            | "image/gif",
          data: base64,
        });
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  /**
   * 画像を追加
   */
  const addImages = async (files: File[]) => {
    const remainingSlots = MAX_IMAGES - attachedImages.length;
    if (remainingSlots <= 0) return;

    const filesToAdd = files.slice(0, remainingSlots);
    const newImages = await Promise.all(filesToAdd.map(fileToBase64));
    setAttachedImages((prev) => [...prev, ...newImages]);
  };

  /**
   * 画像を削除
   */
  const removeImage = (index: number) => {
    setAttachedImages((prev) => prev.filter((_, i) => i !== index));
  };

  /**
   * メッセージ送信
   */
  const handleSend = async () => {
    if (!inputValue.trim() || isLoading) return;

    const message = inputValue;
    const images = attachedImages.length > 0 ? [...attachedImages] : undefined;
    setInputValue("");
    setAttachedImages([]);
    await sendChat(message, images);
  };

  return (
    <>
      <Transition mounted={opened} transition="slide-left" duration={300}>
        {(styles) => (
          <Paper
            shadow="xl"
            radius="lg"
            style={{
              ...styles,
              position: "fixed",
              right: 16,
              top: 76, // ヘッダー + 余白
              bottom: 16,
              width: 480,
              zIndex: 200,
              display: "flex",
              flexDirection: "column",
              border: "1px solid var(--mantine-color-default-border)",
            }}
            className="assistant-panel"
          >
            {/* ヘッダー */}
            <ChatPanelHeader
              currentSessionId={currentSessionId}
              onClose={onClose}
              onOpenHistory={() => setHistoryOpened(true)}
            />

            {/* メッセージエリア */}
            <ScrollArea style={{ flex: 1 }} p="md" viewportRef={scrollAreaRef}>
              {messages.length === 0 ? (
                <EmptyChatState />
              ) : (
                <Stack gap="md">
                  {messages.map((msg, index) => (
                    <MessageBubble key={index} message={msg} />
                  ))}
                  {isLoading && <TypingIndicator />}
                </Stack>
              )}
            </ScrollArea>

            {/* エラー表示 */}
            {error && (
              <Alert
                color="red"
                icon={<AlertCircle size={16} />}
                withCloseButton
                onClose={clearError}
                mx="md"
                mb="sm"
              >
                {error}
              </Alert>
            )}

            {/* 入力エリア */}
            <ChatInputArea
              inputValue={inputValue}
              onInputChange={setInputValue}
              attachedImages={attachedImages}
              onAddImages={addImages}
              onRemoveImage={removeImage}
              onSend={handleSend}
              isLoading={isLoading}
            />
          </Paper>
        )}
      </Transition>

      {/* セッション履歴ドロワー */}
      <SessionHistoryDrawer
        opened={historyOpened}
        onClose={() => setHistoryOpened(false)}
        sessions={sessions}
        isLoading={isLoadingSessions}
        currentSessionId={currentSessionId}
        onSelect={selectSession}
        onNewSession={startNewSession}
      />
    </>
  );
}
